import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../../../redux/rootReducer";
import { IProposal } from "../../../../types/IProposal";
import { LikeResponse } from "../../../../types/response/LikeResponse";
import BillsService from "../services/billsService";

type FetchProposalsDto = {
  documentIds: number[];
  teamId: number;
};

type SaveProposalStateDto = {
  proposalId?: number;
  uuid: string;
  refNodeJson: string;
  documentId: number;
  teamId: number;
  content: string;
  isAdmin?: number;
};

type PublishProposalDto = {
  proposalId: number;
  userId: number;
};

export const fetchProposals = createAsyncThunk<IProposal[], FetchProposalsDto>(
  "proposals/fetch",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await BillsService.getDocumentProposals(payload);

      return data as IProposal[];
    } catch (error) {
      return rejectWithValue("Failed to fetch proposals...");
    }
  }
);

export const saveProposalState = createAsyncThunk<
  IProposal,
  SaveProposalStateDto
>("proposals/saveState", async (payload, { rejectWithValue }) => {
  const { proposalId, isAdmin, ...restPayload } = payload;

  try {
    let response: any;

    if (proposalId) {
      response = isAdmin
        ? await BillsService.adminSaveProposalState(
            Number(proposalId),
            restPayload
          )
        : await BillsService.saveProposalState(Number(proposalId), restPayload);
    } else {
      response = await BillsService.addProposalState(payload);
    }

    const { data } = response;

    return data as IProposal;
  } catch (error) {
    return rejectWithValue("PROPOSALS.FAILED_TO_SAVE");
  }
});

export const publishProposal = createAsyncThunk<IProposal, PublishProposalDto>(
  "proposals/publish",
  async (payload, { rejectWithValue }) => {
    const { proposalId, userId } = payload;

    try {
      const { data } = await BillsService.publishProposal(proposalId, userId);

      return data as IProposal;
    } catch (error) {
      return rejectWithValue("Failed to publish proposal...");
    }
  }
);

export const deleteProposal = createAsyncThunk<IProposal, number>(
  "proposals/delete",
  async (proposalId, { rejectWithValue }) => {
    try {
      const { data } = await BillsService.deleteProposal(proposalId);

      return data as IProposal;
    } catch (error) {
      return rejectWithValue("Failed to delete proposal...");
    }
  }
);

export const voteProposalState = createAsyncThunk<
  LikeResponse,
  { proposalId: number; value: -1 | 0 | 1 }
>("proposals/vote", async (payload, { rejectWithValue }) => {
  try {
    const { proposalId, value } = payload;
    const { data } = await BillsService.likeProposal(proposalId, value);

    return data as LikeResponse;
  } catch (error) {
    return rejectWithValue("Failed to vote...");
  }
});

const proposalsAdapter = createEntityAdapter<IProposal>({
  selectId: (proposal) => proposal.uuid,
});

export const proposalsSelectors = proposalsAdapter.getSelectors(
  (state: RootState) => state.proposals
);

export const proposalsStateSelector = (state: RootState) => state.proposals;

export const proposalsSlice = createSlice({
  name: "proposals",
  initialState: proposalsAdapter.getInitialState({
    loadingProposals: "idle",
    loadProposalsError: null,
    currentRequestId: undefined,
    currentProposalId: undefined,
    saveProposalStateInProgress: "idle",
    savingProposalStateError: null,
    deletableProposalId: undefined,
    publishableProposalId: undefined,
  }),
  reducers: {
    setDeletableProposalId(state, action) {
      state.deletableProposalId = action.payload;
    },
    setPublishableProposalId(state, action) {
      state.publishableProposalId = action.payload;
    },
    clearPublishableProposalId(state) {
      state.publishableProposalId = undefined;
    },
    clearErrors(state) {
      state.savingProposalStateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch proposals
      .addCase(fetchProposals.pending, (state, action) => {
        if (state.loadingProposals === "idle") {
          state.loadingProposals = "pending";
          state.currentRequestId = action.meta.requestId;
          state.loadProposalsError = null;
        }
      })
      .addCase(fetchProposals.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.loadingProposals === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.loadingProposals = "idle";
          proposalsAdapter.setAll(state, action.payload);
          state.currentRequestId = undefined;
        }
      })
      .addCase(fetchProposals.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.loadingProposals === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.loadingProposals = "idle";
          state.loadProposalsError = action.payload;
          state.currentRequestId = undefined;
        }
      })
      // Save proposal state
      .addCase(saveProposalState.pending, (state, action) => {
        if (state.saveProposalStateInProgress === "idle") {
          state.saveProposalStateInProgress = "pending";
          state.currentRequestId = action.meta.requestId;
          state.savingProposalStateError = null;
          state.currentProposalId = action.meta.arg.proposalId || "temp_id";
        }
      })
      .addCase(saveProposalState.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.saveProposalStateInProgress === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.saveProposalStateInProgress = "idle";
          proposalsAdapter.upsertOne(state, action.payload);
          state.currentRequestId = undefined;
          state.currentProposalId = undefined;
        }
      })
      .addCase(saveProposalState.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.saveProposalStateInProgress === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.saveProposalStateInProgress = "idle";
          state.savingProposalStateError = action.payload;
          state.currentRequestId = undefined;
          state.currentProposalId = undefined;
        }
      })
      // Publish proposal
      .addCase(publishProposal.pending, (state, action) => {
        if (state.saveProposalStateInProgress === "idle") {
          state.saveProposalStateInProgress = "pending";
          state.currentRequestId = action.meta.requestId;
          state.savingProposalStateError = null;
          state.currentProposalId = action.meta.arg.proposalId;
        }
      })
      .addCase(publishProposal.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.saveProposalStateInProgress === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.saveProposalStateInProgress = "idle";
          proposalsAdapter.upsertOne(state, action.payload);
          state.currentRequestId = undefined;
          state.currentProposalId = undefined;
        }
      })
      .addCase(publishProposal.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.saveProposalStateInProgress === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.saveProposalStateInProgress = "idle";
          state.savingProposalStateError = action.payload;
          state.currentRequestId = undefined;
          state.currentProposalId = undefined;
        }
      })
      // Delete proposal
      .addCase(deleteProposal.pending, (state, action) => {
        if (state.saveProposalStateInProgress === "idle") {
          state.saveProposalStateInProgress = "pending";
          state.currentRequestId = action.meta.requestId;
          state.savingProposalStateError = null;
          state.currentProposalId = action.meta.arg;
        }
      })
      .addCase(deleteProposal.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.saveProposalStateInProgress === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.saveProposalStateInProgress = "idle";
          proposalsAdapter.removeOne(state, action.payload.uuid);
          state.currentRequestId = undefined;
          state.currentProposalId = undefined;
          state.deletableProposalId = undefined;
        }
      })
      .addCase(deleteProposal.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (
          state.saveProposalStateInProgress === "pending" &&
          state.currentRequestId === requestId
        ) {
          state.saveProposalStateInProgress = "idle";
          state.savingProposalStateError = action.payload;
          state.currentRequestId = undefined;
          state.currentProposalId = undefined;
          state.deletableProposalId = undefined;
        }
      })
      // Vote
      .addCase(voteProposalState.fulfilled, (state, action) => {
        const proposals = proposalsAdapter.getSelectors().selectAll(state);

        const originalProposal = proposals.find(
          (p) => p.id === action.payload.proposalId
        );
        const updatedLikes = originalProposal.likes.filter((l) => {
          return l.userId !== action.payload.userId;
        });

        updatedLikes.push(action.payload);

        const proposal = { ...originalProposal, likes: updatedLikes };

        proposalsAdapter.upsertOne(state, proposal);
      });
  },
});

export const {
  setDeletableProposalId,
  setPublishableProposalId,
  clearPublishableProposalId,
  clearErrors,
} = proposalsSlice.actions;
